// pages/stock/StockHome.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Alert,
    Badge,
    Box,
    Flex,
    Heading,
    HStack,
    SimpleGrid,
    Skeleton,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    FiActivity,
    FiArrowRight,
    FiBarChart2,
    FiBookOpen,
    FiClock,
    FiFileText,
    FiLock,
    FiRefreshCw,
    FiTrendingUp,
} from 'react-icons/fi';

// Organized imports
import { useStockInfo } from '../../hooks/useStockInfo';
import { useSecFilingsData } from '../../hooks/useSecFilingsData';
import { ExternalLink } from '../../components/ExternalLink';
import StockNotes from './StockNotes';
import { useColorModeValue } from '../../components/ui/color-mode';
import { getPublicStockCustomMetrics } from '../../api';

// Group assets for cleaner imports
import openinsiderLogo from '../../assets/openinsider_favicon.png';
import yahooFinanceLogo from '../../assets/yahoo_finance_logo.png';
import seekingAlphaLogo from '../../assets/seeking_alpha_favicon.png';
import secEdgarLogoPNG from '../../assets/SEC_EDGAR_favicon.png';
import zacksLogoPNG from '../../assets/zacks_logo.png';

const sortFilingsByDateDesc = (filings) =>
    [...filings].sort((a, b) => {
        const dateA = a?.filingDateValue || Date.parse(a?.filingDate || '') || 0;
        const dateB = b?.filingDateValue || Date.parse(b?.filingDate || '') || 0;
        return dateB - dateA;
    });

const isInsiderForm = (filing) => {
    const formType = String(filing?.formTypeKey || filing?.formType || '')
        .trim()
        .toUpperCase()
        .replace(/^FORM\s+/, '');

    return (
        formType === '3' ||
        formType === '4' ||
        formType === '5' ||
        formType === '144' ||
        formType.startsWith('3/') ||
        formType.startsWith('4/') ||
        formType.startsWith('5/')
    );
};

const formatDate = (value) => {
    if (!value) return 'No date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value).split('T')[0] || 'No date';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return `${numeric.toFixed(1).replace(/\.0$/, '')}%`;
};

const formatLargeNumber = (value, prefix = '$') => {
    const numeric = Number(String(value ?? '').replace(/,/g, ''));
    if (!Number.isFinite(numeric)) return null;

    const abs = Math.abs(numeric);
    const sign = numeric < 0 ? '-' : '';
    if (abs >= 1e12) return `${sign}${prefix}${(abs / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${sign}${prefix}${(abs / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${sign}${prefix}${(abs / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}${prefix}${(abs / 1e3).toFixed(1)}K`;
    return `${sign}${prefix}${abs.toFixed(0)}`;
};

const getSavedScenarioLabel = (saved) => {
    if (saved?.name) return saved.name;
    const rawDate = saved?.updated_at || saved?.created_at;
    return `Unnamed scenario - ${formatDate(rawDate)}`;
};

function SectionPanel({ title, actionLabel, to, children }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const bg = useColorModeValue('white', 'gray.900');

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" bg={bg} p={{ base: 4, md: 5 }}>
            <Flex justify="space-between" align="center" gap={3} mb={4}>
                <Heading size="md">{title}</Heading>
                {to && actionLabel && (
                    <HStack
                        as={RouterLink}
                        to={to}
                        gap={1}
                        color="blue.600"
                        fontSize="sm"
                        fontWeight="semibold"
                        flexShrink={0}
                    >
                        <Text>{actionLabel}</Text>
                        <FiArrowRight />
                    </HStack>
                )}
            </Flex>
            {children}
        </Box>
    );
}

function DashboardTile({ icon: Icon, title, preview, to, colorPalette = 'blue' }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const bg = useColorModeValue('white', 'gray.900');
    const hoverBg = useColorModeValue('gray.50', 'gray.800');
    const mutedText = useColorModeValue('gray.600', 'gray.400');

    return (
        <Box
            as={RouterLink}
            to={to}
            display="flex"
            flexDirection="column"
            minH="150px"
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            bg={bg}
            p={4}
            transition="border-color 120ms ease, background 120ms ease, transform 120ms ease"
            _hover={{ borderColor: `${colorPalette}.400`, bg: hoverBg, transform: 'translateY(-1px)' }}
        >
            <Flex justify="space-between" align="flex-start" gap={3}>
                <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    boxSize="38px"
                    borderRadius="md"
                    bg={`${colorPalette}.100`}
                    color={`${colorPalette}.700`}
                    flexShrink={0}
                >
                    <Icon />
                </Box>
                <FiArrowRight />
            </Flex>
            <Heading
                as="h2"
                mt={4}
                mb={2}
                fontSize={{ base: '2xl', md: '3xl' }}
                lineHeight="1.12"
                letterSpacing="0"
            >
                {title}
            </Heading>
            <Text fontSize="sm" color={mutedText} lineHeight="1.45">
                {preview}
            </Text>
        </Box>
    );
}

function FilingPreviewList({ filings, loading, error, emptyMessage }) {
    const mutedText = useColorModeValue('gray.600', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    if (loading) {
        return (
            <VStack align="stretch" gap={3}>
                <Skeleton height="48px" />
                <Skeleton height="48px" />
                <Skeleton height="48px" />
            </VStack>
        );
    }

    if (error) {
        return (
            <Alert.Root status="warning">
                <Alert.Indicator />
                <Alert.Content>{error}</Alert.Content>
            </Alert.Root>
        );
    }

    if (!filings.length) {
        return <Text color={mutedText}>{emptyMessage}</Text>;
    }

    return (
        <VStack align="stretch" gap={3}>
            {filings.map((filing) => {
                const aiTags = filing.ai?.masterTags?.length ? filing.ai.masterTags : filing.ai?.tags || [];
                return (
                    <Box key={filing.id} borderBottom="1px solid" borderColor={borderColor} pb={3}>
                        <Flex gap={2} align="center" wrap="wrap" mb={1}>
                            <Badge colorPalette="blue" variant="subtle">{filing.formType}</Badge>
                            <Text fontSize="sm" color={mutedText}>{formatDate(filing.filingDate)}</Text>
                        </Flex>
                        <Text fontSize="sm" fontWeight="medium" lineClamp={2}>
                            {filing.ai?.summary140 || filing.description || 'SEC filing'}
                        </Text>
                        {aiTags.length > 0 && (
                            <HStack gap={2} mt={2} wrap="wrap">
                                {aiTags.slice(0, 3).map((tag) => (
                                    <Badge key={tag} colorPalette="gray" variant="outline" fontSize="xs">
                                        {String(tag).replace(/_/g, ' ')}
                                    </Badge>
                                ))}
                            </HStack>
                        )}
                    </Box>
                );
            })}
        </VStack>
    );
}

function ReverseDcfPreview({ isAuthenticated, isAuthLoading, saves, loading, error, to }) {
    const mutedText = useColorModeValue('gray.600', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    if (isAuthLoading) {
        return (
            <HStack gap={2} color={mutedText}>
                <Spinner size="sm" />
                <Text fontSize="sm">Checking saved scenarios...</Text>
            </HStack>
        );
    }

    if (!isAuthenticated) {
        return (
            <HStack gap={2} color={mutedText}>
                <FiLock />
                <Text fontSize="sm">Sign in to see saved reverse DCF scenarios.</Text>
            </HStack>
        );
    }

    if (loading) {
        return (
            <VStack align="stretch" gap={3}>
                <Skeleton height="44px" />
                <Skeleton height="44px" />
            </VStack>
        );
    }

    if (error) {
        return (
            <Alert.Root status="warning">
                <Alert.Indicator />
                <Alert.Content>{error}</Alert.Content>
            </Alert.Root>
        );
    }

    if (!saves.length) {
        return (
            <Text color={mutedText}>
                No saved scenarios yet. Open Reverse DCF to save assumptions for this ticker.
            </Text>
        );
    }

    return (
        <VStack align="stretch" gap={3}>
            {saves.slice(0, 3).map((saved) => {
                const assumptions = saved.assumptions || {};
                const wacc = formatPercent(assumptions.wacc);
                const terminalGrowth = formatPercent(assumptions.terminalGrowth);
                const fcf = formatLargeNumber(assumptions.fcfTtm);
                const details = [wacc && `WACC ${wacc}`, terminalGrowth && `Terminal ${terminalGrowth}`, fcf && `FCF ${fcf}`]
                    .filter(Boolean)
                    .join(' • ');

                return (
                    <Box
                        key={saved.id || `${saved.name}-${saved.updated_at}`}
                        as={RouterLink}
                        to={to}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        p={3}
                        _hover={{ borderColor: 'green.400' }}
                    >
                        <Flex justify="space-between" gap={3} align="flex-start">
                            <Box minW={0}>
                                <Text fontWeight="semibold" truncate>{getSavedScenarioLabel(saved)}</Text>
                                <Text fontSize="xs" color={mutedText}>
                                    Updated {formatDate(saved.updated_at || saved.created_at)}
                                </Text>
                                {details && (
                                    <Text fontSize="sm" color={mutedText} mt={1}>{details}</Text>
                                )}
                            </Box>
                            <FiArrowRight />
                        </Flex>
                    </Box>
                );
            })}
        </VStack>
    );
}

function StockHome() {
    const { ticker } = useParams();

    // Use safe defaults to ensure hooks are always called with valid values
    const upperCaseTicker = ticker ? ticker.toUpperCase() : '';
    const lowerCaseTicker = ticker ? ticker.toLowerCase() : '';
    const { data: stockInfo, loading, error } = useStockInfo(upperCaseTicker);
    const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
    const [reverseDcfSaves, setReverseDcfSaves] = useState([]);
    const [reverseDcfLoading, setReverseDcfLoading] = useState(false);
    const [reverseDcfError, setReverseDcfError] = useState('');
    const [publicMetricsCount, setPublicMetricsCount] = useState(0);
    const apiUrl = useMemo(() => (process.env.REACT_APP_API_URL || '').replace(/\/+$/, ''), []);
    const secApiBaseUrl = apiUrl;
    const { filings, isFetching: filingsLoading, error: filingsError } = useSecFilingsData(upperCaseTicker, secApiBaseUrl);

    const sortedFilings = useMemo(() => sortFilingsByDateDesc(filings), [filings]);
    const latestFilings = useMemo(() => sortedFilings.slice(0, 5), [sortedFilings]);
    const insiderFilings = useMemo(
        () => sortedFilings.filter(isInsiderForm).slice(0, 3),
        [sortedFilings]
    );

    useEffect(() => {
        if (!isAuthenticated || !upperCaseTicker) {
            setReverseDcfSaves([]);
            setReverseDcfLoading(false);
            setReverseDcfError('');
            return undefined;
        }

        let cancelled = false;

        async function fetchReverseDcfSaves() {
            setReverseDcfLoading(true);
            setReverseDcfError('');

            try {
                const token = await getAccessTokenSilently({ detailedResponse: false });
                const { data } = await axios.get(
                    `${apiUrl}/api/reverse-dcf/saves?ticker=${encodeURIComponent(upperCaseTicker)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (cancelled) return;

                const saves = Array.isArray(data) ? data : data?.saves || [];
                setReverseDcfSaves(
                    [...saves].sort((a, b) => {
                        const dateA = Date.parse(a?.updated_at || a?.created_at || '') || 0;
                        const dateB = Date.parse(b?.updated_at || b?.created_at || '') || 0;
                        return dateB - dateA;
                    })
                );
            } catch (err) {
                if (cancelled) return;
                const message =
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err?.message ||
                    'Could not load saved reverse DCF scenarios.';
                setReverseDcfError(message);
            } finally {
                if (!cancelled) {
                    setReverseDcfLoading(false);
                }
            }
        }

        fetchReverseDcfSaves();

        return () => {
            cancelled = true;
        };
    }, [apiUrl, getAccessTokenSilently, isAuthenticated, upperCaseTicker]);

    useEffect(() => {
        if (!upperCaseTicker) {
            setPublicMetricsCount(0);
            return undefined;
        }

        let cancelled = false;

        async function fetchPublicMetrics() {
            try {
                const payload = await getPublicStockCustomMetrics(upperCaseTicker);
                if (cancelled) return;
                const metrics = Array.isArray(payload?.metrics) ? payload.metrics : [];
                setPublicMetricsCount(metrics.length);
            } catch (err) {
                if (!cancelled) {
                    setPublicMetricsCount(0);
                }
            }
        }

        fetchPublicMetrics();

        return () => {
            cancelled = true;
        };
    }, [upperCaseTicker]);

    // useMemo will prevent this array from being redefined on every render
    const externalLinks = useMemo(() => {
        // Handle missing ticker gracefully
        if (!ticker) {
            return [];
        }
        
        const lowerCaseTicker = ticker.toLowerCase();
        
        // Define links as a data structure. This is easier to maintain.
        const links = [
            {
                href: `http://openinsider.com/search?q=${lowerCaseTicker}`,
                logo: openinsiderLogo,
                text: 'Open Insider (Insider Trading)',
            },
            {
                href: `https://seekingalpha.com/symbol/${upperCaseTicker}`,
                logo: seekingAlphaLogo,
                text: 'Seeking Alpha Profile & News',
            },
            {
                href: `https://finance.yahoo.com/quote/${upperCaseTicker}`,
                logo: yahooFinanceLogo,
                text: 'Yahoo Finance Quote & Chart',
            },
            {
                // This link is conditionally created based on the fetched CIK
                href: stockInfo?.cik
                    ? `https://www.sec.gov/edgar/browse/?CIK=${stockInfo.cik}&owner=exclude`
                    : null,
                logo: secEdgarLogoPNG,
                text: 'SEC EDGAR Filings',
            },
            {
                href: `https://www.zacks.com/stock/research/${upperCaseTicker}/earnings-calendar`,
                logo: zacksLogoPNG,
                text: 'Zacks Earnings Calendar',
            },  
        ];
        
        // Filter out any links that don't have an href
        return links.filter(link => link.href);
    }, [ticker, upperCaseTicker, stockInfo]);

    const routes = useMemo(() => {
        const stockBase = `/stock/${lowerCaseTicker}`;
        return {
            analysis: `${stockBase}/analysis`,
            reverseDcf: `${stockBase}/reverse-dcf`,
            charts: `${stockBase}/charts`,
            snapshot: `${stockBase}/snapshot`,
            financials: `${stockBase}/financials/balance-sheet`,
            filings: `${stockBase}/sec-filings`,
            publicMetrics: `/stocks/${lowerCaseTicker}/metrics`,
        };
    }, [lowerCaseTicker]);

    const dashboardTiles = useMemo(() => {
        const tiles = [
            {
                title: 'Regular DCF / Analysis',
                preview: 'Review free cash flow history, projection inputs, intrinsic value, and AI rating tools.',
                to: routes.analysis,
                icon: FiBarChart2,
                colorPalette: 'teal',
            },
            {
                title: 'Reverse DCF',
                preview: isAuthenticated && reverseDcfSaves.length
                    ? `${reverseDcfSaves.length} saved scenario${reverseDcfSaves.length === 1 ? '' : 's'} for ${upperCaseTicker}.`
                    : 'Work backward from the current market price and save assumption sets.',
                to: routes.reverseDcf,
                icon: FiRefreshCw,
                colorPalette: 'green',
            },
            {
                title: 'SEC Filings',
                preview: filingsLoading
                    ? 'Loading latest filings...'
                    : latestFilings.length
                        ? `${latestFilings.length} recent filing${latestFilings.length === 1 ? '' : 's'} ready to review.`
                        : 'Read annual reports, current reports, proxies, and AI filing summaries.',
                to: routes.filings,
                icon: FiFileText,
                colorPalette: 'blue',
            },
            {
                title: 'Charts',
                preview: 'Scan price action, moving averages, and market context for this ticker.',
                to: routes.charts,
                icon: FiTrendingUp,
                colorPalette: 'purple',
            },
            {
                title: 'Snapshot',
                preview: 'Practice valuation from a historical date with hindsight-safe filings and prices.',
                to: routes.snapshot,
                icon: FiClock,
                colorPalette: 'pink',
            },
            {
                title: 'Financial Statements',
                preview: 'Open income statement, balance sheet, and cash flow statement views.',
                to: routes.financials,
                icon: FiBookOpen,
                colorPalette: 'orange',
            },
        ];

        if (publicMetricsCount > 0) {
            tiles.push({
                title: 'Public KPI Data',
                preview: `${publicMetricsCount} approved operating metric${publicMetricsCount === 1 ? '' : 's'} with filing-sourced historical data.`,
                to: routes.publicMetrics,
                icon: FiActivity,
                colorPalette: 'cyan',
            });
        }

        return tiles;
    }, [filingsLoading, isAuthenticated, latestFilings.length, publicMetricsCount, reverseDcfSaves.length, routes, upperCaseTicker]);

    // Handle missing ticker gracefully (after hooks are called)
    if (!ticker) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        Invalid ticker symbol. Please provide a valid stock ticker.
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box p={4}>
                <Skeleton height="40px" width="300px" mb={4} />
                <Skeleton height="20px" width="400px" mb={6} />
                <SimpleGrid columns={{ base: 1, sm: 2, xl: 3 }} gap={4}>
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                    <Skeleton height="150px" />
                </SimpleGrid>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        {error}
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }
    
    // Handle case where stockInfo is null/undefined (shouldn't happen if error is set, but be defensive)
    if (!stockInfo) {
        return (
            <Box p={4}>
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                        Could not load stock information for ticker "{upperCaseTicker}". Please verify the ticker symbol is correct.
                    </Alert.Content>
                </Alert.Root>
            </Box>
        );
    }
    
    return (
        <Box p={{ base: 1, md: 4 }}>
            <Box mb={6}>
                <Badge colorPalette="blue" variant="subtle" mb={3}>{upperCaseTicker}</Badge>
                <Heading size={{ base: 'xl', md: '2xl' }}>
                    {upperCaseTicker}: {stockInfo.company_name || 'Unknown Company'}
                </Heading>
                <Text fontSize={{ base: 'md', md: 'lg' }} mt={2} color="gray.600">
                    Valuation dashboard for {upperCaseTicker}
                </Text>
            </Box>

            <Box mb={6}>
                <SectionPanel title="External Resources">
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                        {externalLinks.map((link) => (
                            <Box key={link.text} fontSize="sm">
                                <ExternalLink
                                    href={link.href}
                                    logoSrc={link.logo}
                                    altText={`${link.text} Logo`}
                                >
                                    {link.text}
                                </ExternalLink>
                            </Box>
                        ))}
                    </SimpleGrid>
                </SectionPanel>
            </Box>

            <SimpleGrid columns={{ base: 1, sm: 2, xl: 3 }} gap={4} mb={6}>
                {dashboardTiles.map((tile) => (
                    <DashboardTile key={tile.title} {...tile} />
                ))}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} gap={4} mb={6}>
                <SectionPanel title="Reverse DCF Scenarios" actionLabel="Open Reverse DCF" to={routes.reverseDcf}>
                    <ReverseDcfPreview
                        isAuthenticated={isAuthenticated}
                        isAuthLoading={isAuthLoading}
                        saves={reverseDcfSaves}
                        loading={reverseDcfLoading}
                        error={reverseDcfError}
                        to={routes.reverseDcf}
                    />
                </SectionPanel>

                <SectionPanel title="Analysis Workspace" actionLabel="Open Analysis" to={routes.analysis}>
                    <VStack align="stretch" gap={3}>
                        <Text color="gray.600">
                            Build a regular DCF from cash-flow history, projection assumptions, and intrinsic value outputs.
                        </Text>
                        <HStack gap={2} wrap="wrap">
                            <Badge colorPalette="teal" variant="subtle">Cash flow history</Badge>
                            <Badge colorPalette="teal" variant="subtle">Projection inputs</Badge>
                            <Badge colorPalette="teal" variant="subtle">AI rating</Badge>
                        </HStack>
                    </VStack>
                </SectionPanel>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, xl: 2 }} gap={4} mb={6}>
                <SectionPanel title="Latest SEC Filings" actionLabel="View all filings" to={routes.filings}>
                    <FilingPreviewList
                        filings={latestFilings.slice(0, 5)}
                        loading={filingsLoading}
                        error={filingsError}
                        emptyMessage={`No SEC filings found for ${upperCaseTicker}.`}
                    />
                </SectionPanel>

                <SectionPanel title="Insider Activity" actionLabel="Open filings view" to={routes.filings}>
                    <Flex gap={2} align="center" mb={3} color="gray.600">
                        <FiActivity />
                        <Text fontSize="sm">Forms 3, 4, 5, and 144 from the SEC filings feed.</Text>
                    </Flex>
                    <FilingPreviewList
                        filings={insiderFilings}
                        loading={filingsLoading}
                        error={filingsError}
                        emptyMessage={`No recent insider-related filings found for ${upperCaseTicker}.`}
                    />
                </SectionPanel>
            </SimpleGrid>

            <Box mt={6}>
                <Heading size="md" mb={4}>Company Notes</Heading>
                <StockNotes ticker={ticker} />
            </Box>

        </Box>
    );
}

export default StockHome;
